import { FC, useCallback } from 'react'
import { Form } from 'react-final-form'

// components
import { EmailInputField, PasswordInputField } from 'components/login/common'
import { IconButton, Button } from 'components/common'

// utils
import { validateUsername, validateRequiredKey } from 'helpers/validators'

import type { Credentials } from 'types/user'
import type { Payload } from 'types/common'

import scss from '../index.module.scss'

export interface EmailPasswordFormProps {
  onSubmit: (values: Credentials) => Promise<{
    'FINAL_FORM/form-error': string
  } | null>
  showEmailField?: boolean
  autoFocusOnPassword?: boolean
  submitButtonText?: string
}

const EmailPasswordForm: FC<EmailPasswordFormProps> = ({
  onSubmit,
  showEmailField = true,
  autoFocusOnPassword,
  submitButtonText = 'Sign In',
}) => {
  const validate = useCallback(
    (values: Credentials) => {
      const errors = {} as Payload
      if (showEmailField) {
        validateUsername(values, errors)
      }

      validateRequiredKey('password', 'Password')(
        values as unknown as Payload,
        errors
      )
      return errors
    },
    [showEmailField]
  )

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      render={({
        handleSubmit,
        submitting,
        submitError,
        hasValidationErrors,
      }) => (
        <form onSubmit={handleSubmit} className={scss.form}>
          {submitError && (
            <div className='alert bg-danger d-flex align-items-center text-white'>
              <IconButton
                disabled
                icon='MdWarning'
                size={20}
                style={{ color: '#fff', marginRight: '8px' }}
              />
              <div>{submitError}</div>
            </div>
          )}
          {showEmailField && (
            <div className={`text-start ${scss.row}`}>
              <EmailInputField />
            </div>
          )}
          <div className={`text-start ${scss.row}`}>
            <PasswordInputField autoFocus={autoFocusOnPassword} />
          </div>
          <Button
            type='submit'
            disabled={submitting || hasValidationErrors}
            block
            className={`${scss.submit} mx-0`}
            testId='submit'
            isIconFixed
            isLoading={submitting}
            icon='ArrowRightNextIcon'
          >
            {submitButtonText}
          </Button>
        </form>
      )}
    />
  )
}

export default EmailPasswordForm
